import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { StoreProvider, useStore } from "@/lib/store";
import { AuthProvider } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { API } from "@/lib/api";
import SignIn from "@/pages/SignIn";
import Landing from "@/pages/Landing";
import Headquarters from "@/pages/Headquarters";
import MissionControl from "@/pages/MissionControl";
import MyEmployees from "@/pages/MyEmployees";
import EmployeeProfile from "@/pages/EmployeeProfile";
import Departments from "@/pages/Departments";
import ApprovalCenter from "@/pages/ApprovalCenter";
import ActivityLog from "@/pages/ActivityLog";
import HireEmployee from "@/pages/HireEmployee";
import TrainingLab from "@/pages/TrainingLab";
import AssociateTraining from "@/pages/AssociateTraining";
import AboutCompany from "@/pages/AboutCompany";
import AddLogins from "@/pages/AddLogins";
import CallLog from "@/pages/CallLog";
import Contacts from "@/pages/Contacts";
import Settings from "@/pages/Settings";
import SupportInbox from "@/pages/SupportInbox";

function isGuest() {
  try { return localStorage.getItem("iq-guest") === "1"; } catch (_) { return false; }
}

function LoadingScreen({ label = "Loading Digital Headquarters…" }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[#030A16]">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#00E5FF]/30 border-t-[#00E5FF]" />
        <div className="font-mono text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}

// Handles the return trip from Emergent Google OAuth (#session_id=...).
function AuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const res = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "X-Session-ID": sid },
          credentials: "include",
        });
        if (res.status === 403) {
          window.history.replaceState({}, "", "/signin");
          navigate("/signin?denied=1", { replace: true });
          return;
        }
        if (!res.ok) throw new Error("session");
        const user = await res.json();
        try { localStorage.removeItem("iq-guest"); } catch (_) {}
        window.history.replaceState({}, "", "/hq");
        navigate("/hq", { replace: true, state: { user } });
      } catch (_) {
        window.history.replaceState({}, "", "/signin");
        navigate("/signin?error=1", { replace: true });
      }
    })();
  }, [navigate]);
  return <LoadingScreen label="Signing you in…" />;
}

function Protected({ children }) {
  const { state } = useStore();
  const location = useLocation();
  const [authed, setAuthed] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user || isGuest()) { setAuthed(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!r.ok) throw new Error("unauth");
        if (!cancelled) setAuthed(true);
      } catch (_) {
        if (!cancelled) setAuthed(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authed === false) return <Navigate to="/signin" replace />;
  if (authed === null || !state.loaded) return <LoadingScreen />;
  return <AppShell>{children}</AppShell>;
}

function AppRoutes() {
  const location = useLocation();
  // Process the OAuth callback synchronously during render (prevents races).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/hq" element={<Protected><Headquarters /></Protected>} />
      <Route path="/mission" element={<Protected><MissionControl /></Protected>} />
      <Route path="/employees" element={<Protected><MyEmployees /></Protected>} />
      <Route path="/support" element={<Protected><SupportInbox /></Protected>} />
      <Route path="/employees/:id" element={<Protected><EmployeeProfile /></Protected>} />
      <Route path="/departments" element={<Protected><Departments /></Protected>} />
      <Route path="/approvals" element={<Protected><ApprovalCenter /></Protected>} />
      <Route path="/activity" element={<Protected><ActivityLog /></Protected>} />
      <Route path="/hire" element={<Protected><HireEmployee /></Protected>} />
      <Route path="/training" element={<Protected><TrainingLab /></Protected>} />
      <Route path="/associate-training" element={<Protected><AssociateTraining /></Protected>} />
      <Route path="/about" element={<Protected><AboutCompany /></Protected>} />
      <Route path="/logins" element={<Protected><AddLogins /></Protected>} />
      <Route path="/calls" element={<Protected><CallLog /></Protected>} />
      <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <StoreProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0A1326", border: "1px solid rgba(255,255,255,0.1)", color: "#F8FAFC" } }} />
        </AuthProvider>
      </StoreProvider>
    </div>
  );
}

export default App;
